// src/pages/FAQPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown, Search, Package, CreditCard, Truck, 
  RotateCcw, HelpCircle, Shield, Mail, Phone
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';

export default function FAQPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [openFAQId, setOpenFAQId] = useState(null);

  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  const categories = [
    { id: 'all', name: 'Tümü', icon: <HelpCircle size={20} /> },
    { id: 'order', name: 'Sipariş', icon: <Package size={20} /> },
    { id: 'payment', name: 'Ödeme', icon: <CreditCard size={20} /> },
    { id: 'shipping', name: 'Kargo', icon: <Truck size={20} /> },
    { id: 'return', name: 'İade', icon: <RotateCcw size={20} /> },
    { id: 'account', name: 'Hesap', icon: <Shield size={20} /> },
  ];

  const faqs = [
    // Sipariş
    {
      id: 1,
      category: 'order',
      question: 'Nasıl sipariş verebilirim?',
      answer: 'Sipariş vermek için öncelikle istediğiniz ürünü sepete ekleyin. Sepet sayfasında "Siparişi Tamamla" butonuna tıklayın. Giriş yapın veya misafir olarak devam edin. Teslimat adresinizi girin ve ödeme bilgilerinizi tamamlayın. Siparişiniz onaylandıktan sonra e-posta ile bilgilendirme alacaksınız.'
    },
    {
      id: 2,
      category: 'order',
      question: 'Siparişimi nasıl takip edebilirim?',
      answer: 'Siparişlerinizi "Hesabım" > "Siparişlerim" bölümünden takip edebilirsiniz. Kargo takip numaranız SMS ve e-posta ile tarafınıza iletilecektir. Bu numara ile kargo firmasının web sitesinden anlık olarak paketinizin konumunu görebilirsiniz.'
    },
    {
      id: 3,
      category: 'order',
      question: 'Siparişimi iptal edebilir miyim?',
      answer: 'Siparişiniz kargoya verilmeden önce iptal edebilirsiniz. "Siparişlerim" sayfasından ilgili siparişin yanındaki "İptal Et" butonuna tıklayın. Kargoya verildikten sonra iptal işlemi yapılamaz, ancak ürünü teslim aldıktan sonra iade edebilirsiniz.'
    },
    {
      id: 4,
      category: 'order',
      question: 'Sipariş onayı ne zaman gelir?',
      answer: 'Siparişiniz başarıyla tamamlandıktan sonra anında e-posta ve SMS ile sipariş onayı alırsınız. Eğer 15 dakika içinde onay mesajı almadıysanız, spam klasörünüzü kontrol edin veya müşteri hizmetlerimizle iletişime geçin.'
    },
    {
      id: 5,
      category: 'order',
      question: 'Minimum sipariş tutarı var mı?',
      answer: 'Hayır, minimum sipariş tutarı yoktur.'
    },
    
    // Ödeme
    {
      id: 6,
      category: 'payment',
      question: 'Hangi ödeme yöntemlerini kabul ediyorsunuz?',
      answer: 'Kredi kartı (Visa, Mastercard, American Express), banka kartı ve kapıda ödeme seçeneklerini kabul ediyoruz. Tüm kredi kartı ödemeleri 3D Secure ile güvenli şekilde alınmaktadır. Ayrıca taksit imkanlarından da fararlanabilirsiniz.'
    },
    {
      id: 7,
      category: 'payment',
      question: 'Taksit yapabilir miyim?',
      answer: 'Evet, kredi kartı ile yapacağınız alışverişlerde taksit seçeneğinden yararlanabilirsiniz. Taksit sayısı bankanıza ve kartınıza göre değişiklik gösterebilir. Ödeme sayfasında kartınıza ait taksit seçeneklerini görebilirsiniz.'
    },
    {
      id: 8,
      category: 'payment',
      question: 'Ödeme güvenliği nasıl sağlanıyor?',
      answer: 'Tüm ödeme işlemleriniz 256-bit SSL sertifikası ile şifrelenmektedir. Kredi kartı bilgileriniz asla sistemimizde saklanmaz. 3D Secure teknolojisi ile ek güvenlik katmanı sağlanmaktadır. Ödeme altyapımız PCI-DSS standartlarına uygundur.'
    },
    {
      id: 9,
      category: 'payment',
      question: 'Fatura kesiliyor mu?',
      answer: 'Evet, tüm siparişleriniz için e-fatura kesilmektedir. Faturanız sipariş tamamlandıktan sonra e-posta adresinize gönderilecektir. Kurumsal fatura talepleriniz için sipariş notunda belirtebilir veya müşteri hizmetlerimizle iletişime geçebilirsiniz.'
    },
    {
      id: 10,
      category: 'payment',
      question: 'Kapıda ödeme yapabilir miyim?',
      answer: 'Evet, kapıda ödeme seçeneği mevcuttur. Kapıda ödeme ile nakit veya kredi kartı ile ödeme yapabilirsiniz. Ancak bazı ürünlerde kapıda ödeme seçeneği kısıtlı olabilir.'
    },

    // Kargo
    {
      id: 11,
      category: 'shipping',
      question: 'Kargo ücreti ne kadar?',
      answer: 'Kargo ücreti ürüne göre farklılık göstermektedir. Kargo ücreti ödeme sayfasında siparişinize otomatik olarak eklenecektir.'
    },
    {
      id: 12,
      category: 'shipping',
      question: 'Kargo ne kadar sürede gelir?',
      answer: 'Siparişleriniz 1-3 iş günü içinde kargoya teslim edilir. Kargo teslimat süresi bölgenize göre 1-3 gün arasında değişmektedir. Toplam olarak siparişiniz 2-6 iş günü içinde elinize ulaşır. Yoğun dönemlerde bu süre uzayabilir.'
    },
    {
      id: 13,
      category: 'shipping',
      question: 'Hangi kargo firması ile çalışıyorsunuz?',
      answer: 'Aras Kargo, Yurtiçi Kargo, MNG Kargo ve PTT Kargo ile çalışmaktayız. Sipariş verilirken bölgenize en uygun kargo firması otomatik olarak seçilir. Kargo tercihinde değişiklik yapamazsınız.'
    },
    {
      id: 14,
      category: 'shipping',
      question: 'Kargom nereye teslim edilir?',
      answer: 'Kargonuz sipariş sırasında belirttiğiniz adrese teslim edilir. Ev veya iş adresi fark etmeksizin teslim alabilirsiniz. Teslim sırasında evde kimse yoksa, kargo firması size ulaşmaya çalışacaktır. Eğer teslimat yapılamazsa, kargo şubesinden teslim alabilirsiniz.'
    },
    {
      id: 15,
      category: 'shipping',
      question: 'Yurtdışına kargo yapıyor musunuz?',
      answer: 'Şu an için sadece Türkiye içerisine kargo hizmeti vermekteyiz. Yurtdışı kargo hizmeti için çalışmalarımız devam etmektedir. Gelişmelerden haberdar olmak için bültenimize abone olabilirsiniz.'
    },

    // İade
    {
      id: 16,
      category: 'return',
      question: 'İade şartları nelerdir?',
      answer: 'Ürünü teslim aldıktan sonra 7 gün içinde iade edebilirsiniz. Ürün kullanılmamış, hasarsız ve orijinal ambalajında olmalıdır. Fatura ve ürün etiketi tam olmalıdır. İade kargo ücreti tarafımızca karşılanır.'
    },
    {
      id: 17,
      category: 'return',
      question: 'İade işlemi nasıl yapılır?',
      answer: 'İade için "Hesabım" > "Siparişlerim" sayfasından ilgili siparişi seçin ve "İade Talebi Oluştur" butonuna tıklayın. İade nedeninizi belirtin. İade onaylandıktan sonra kargo firmamız ürünü adresinizden alacaktır. Ürün tarafımıza ulaştıktan sonra iadəniz işleme alınır.'
    },
    {
      id: 18,
      category: 'return',
      question: 'İade süresi ne kadar?',
      answer: 'İade ettiğiniz ürün depomıza ulaştıktan sonra 3-5 iş günü içinde kontrol edilir ve onaylanır. Onay sonrası ödeme iadeniz 5-10 iş günü içinde kartınıza veya hesabınıza yansır. Toplam süreç yaklaşık 7-15 iş günü sürer.'
    },
    {
      id: 19,
      category: 'return',
      question: 'Hangi ürünler iade edilemez?',
      answer: 'Hijyen kuralları gereği kişisel bakım ürünleri, gıda ürünleri, iç çamaşırı, kozmetik ürünler (açılmış ise) ve özel sipariş üzerine üretilen ürünler iade edilemez. Ayrıca kampanyalı ürünlerde farklı iade koşulları olabilir.'
    },
    {
      id: 20,
      category: 'return',
      question: 'Değişim yapabilir miyim?',
      answer: 'Evet, ürün değişimi yapabilirsiniz. Önce iade işlemini başlatın, ardından yeni ürün için sipariş oluşturun. İade onaylandıktan sonra paranız iade edilecek ve yeni siparişinizi verebileceksiniz. Alternatif olarak müşteri hizmetlerimiz ile iletişime geçerek değişim talebinizi iletebilirsiniz.'
    },

    // Hesap
    {
      id: 21,
      category: 'account',
      question: 'Nasıl üye olabilirim?',
      answer: 'Sağ üstteki "Giriş Yap" butonuna tıklayın, ardından "Kayıt Ol" seçeneğini seçin. Ad, soyad, e-posta ve şifre bilgilerinizi girin. E-posta adresinize gelen onay linkine tıklayarak üyeliğinizi aktifleştirin. Üyeliksiz de alışveriş yapabilirsiniz.'
    },
    {
      id: 22,
      category: 'account',
      question: 'Şifremi unuttum, ne yapmalıyım?',
      answer: 'Giriş sayfasında "Şifremi Unuttum" linkine tıklayın. Kayıtlı e-posta adresinizi girin. E-postanıza şifre sıfırlama linki gelecektir. Link üzerinden yeni şifrenizi belirleyebilirsiniz. Eğer e-posta gelmezse spam klasörünüzü kontrol edin.'
    },
    {
      id: 23,
      category: 'account',
      question: 'Hesap bilgilerimi nasıl güncellerim?',
      answer: '"Hesabım" sayfasından "Profil Bilgilerim" bölümüne girin. Adınız, soyadınız, telefon numaranız ve adres bilgilerinizi buradan güncelleyebilirsiniz. Değişiklikleri kaydettikten sonra bir onay mesajı alacaksınız.'
    },
    {
      id: 24,
      category: 'account',
      question: 'Hesabımı silebilir miyim?',
      answer: 'Evet, hesabınızı silebilirsiniz. "Hesabım" > "Ayarlar" bölümünden "Hesabımı Sil" seçeneğini kullanabilirsiniz. Hesap silme işlemi geri alınamaz ve tüm verileriniz silinir. Aktif siparişiniz varsa, önce bunların tamamlanmasını beklemeniz gerekir.'
    },
    {
      id: 25,
      category: 'account',
      question: 'E-posta bildirimleri nasıl yönetilir?',
      answer: '"Hesabım" > "Bildirim Tercihleri" bölümünden e-posta bildirimlerinizi yönetebilirsiniz. Kampanya duyuruları, sipariş güncellemeleri ve ürün önerileri için ayrı ayrı tercih yapabilirsiniz. Dilediğiniz zaman bu ayarları değiştirebilirsiniz.'
    },
  ];

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="faq-page">
      <Style />
      
      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
      />

      {/* Hero Section */}
      <div className="page-hero">
        <div className="container">
          <div className="hero-content">
            <HelpCircle size={48} className="hero-icon" />
            <h1>Sıkça Sorulan Sorular</h1>
            <p>Aklınıza takılan soruların cevaplarını burada bulabilirsiniz</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">
        <div className="container">
          {/* Search Box */}
          <div className="faq-search-box">
            <Search size={20} className="faq-search-icon" />
            <input
              type="text"
              placeholder="Soru arayın..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Categories */}
          <div className="categories">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`category-btn ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.icon}
                {cat.name}
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="faq-list">
            {filteredFAQs.length > 0 ? (
              filteredFAQs.map(faq => (
                <div 
                  key={faq.id} 
                  className={`faq-item ${openFAQId === faq.id ? 'open' : ''}`}
                >
                  <button 
                    className="faq-question"
                    onClick={() => setOpenFAQId(openFAQId === faq.id ? null : faq.id)}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown className="chevron" size={20} />
                  </button>
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                <HelpCircle size={48} />
                <p>Aradığınız soruya ait sonuç bulunamadı</p>
              </div>
            )}
          </div>

          {/* Contact Box */}
          <div className="contact-box">
            <h3>Cevabını bulamadınız mı?</h3>
            <p>Müşteri hizmetlerimiz size yardımcı olmaktan mutluluk duyar</p>
            <div className="contact-buttons">
              <a href="mailto:info@tronapazarlama.com" className="contact-btn email">
                <Mail size={20} />
                E-posta Gönderin
              </a>
              <a href="tel:08501234567" className="contact-btn phone">
                <Phone size={20} />
                Bizi Arayın
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      * { margin: 0; padding: 0; box-sizing: border-box; }
      
      .faq-page {
        min-height: 100vh;
        background: #f8f9fa;
      }

      .container {
        max-width: 1320px;
        margin: 0 auto;
        padding: 0 20px;
      }

      /* Hero Section */
      .page-hero {
        background: linear-gradient(135deg, #2B2B2B 0%, #1a1a1a 100%);
        padding: 80px 0;
        position: relative;
        overflow: hidden;
      }

      .page-hero::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at 20% 50%, rgba(229, 51, 42, 0.1), transparent 50%);
      }

      .hero-content {
        text-align: center;
        color: #fff;
        position: relative;
        z-index: 1;
      }

      .hero-icon {
        margin-bottom: 24px;
        opacity: 0.9;
      }

      .hero-content h1 {
        font-size: 48px;
        font-weight: 800;
        margin-bottom: 16px;
      }

      .hero-content p {
        font-size: 18px;
        opacity: 0.8;
      }

      /* Page Content */
      .page-content {
        padding: 60px 0;
      }

      /* FAQ Search Box */
      .faq-search-box {
        position: relative;
        max-width: 600px;
        margin: 0 auto 40px;
      }

      .faq-search-icon {
        position: absolute;
        left: 20px;
        top: 50%;
        transform: translateY(-50%);
        color: #868e96;
      }

      .faq-search-box input {
        width: 100%;
        padding: 16px 20px 16px 56px;
        border: 2px solid #e9ecef;
        border-radius: 16px;
        font-size: 16px;
        outline: none;
        transition: all 0.3s;
      }

      .faq-search-box input:focus {
        border-color: #E5332A;
        box-shadow: 0 0 0 4px rgba(229, 51, 42, 0.1);
      }

      /* Categories - MOBİL UYUMLU */
      .categories {
        display: flex;
        gap: 12px;
        justify-content: center;
        flex-wrap: wrap;
        margin-bottom: 48px;
        /* Mobil için scroll desteği */
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        /* Scrollbar gizle ama scroll çalışsın */
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE/Edge */
      }

      .categories::-webkit-scrollbar {
        display: none; /* Chrome, Safari */
      }

      .category-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 24px;
        background: #fff;
        border: 2px solid #e9ecef;
        border-radius: 12px;
        font-size: 14px;
        font-weight: 600;
        color: #495057;
        cursor: pointer;
        transition: all 0.3s;
        white-space: nowrap; /* Metni tek satırda tut */
        flex-shrink: 0; /* Butonların küçülmesini engelle */
      }

      .category-btn:hover {
        border-color: #E5332A;
        color: #E5332A;
        transform: translateY(-2px);
      }

      .category-btn.active {
        background: #E5332A;
        border-color: #E5332A;
        color: #fff;
      }

      /* FAQ List */
      .faq-list {
        max-width: 900px;
        margin: 0 auto;
      }

      .faq-item {
        background: #fff;
        border-radius: 16px;
        margin-bottom: 16px;
        overflow: hidden;
        border: 2px solid #e9ecef;
        transition: all 0.3s;
      }

      .faq-item:hover {
        border-color: #E5332A;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }

      .faq-question {
        width: 100%;
        padding: 24px;
        background: none;
        border: none;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        cursor: pointer;
        text-align: left;
        font-size: 16px;
        font-weight: 600;
        color: #2B2B2B;
      }

      .faq-question .chevron {
        flex-shrink: 0;
        transition: transform 0.3s;
        color: #868e96;
      }

      .faq-item.open .chevron {
        transform: rotate(180deg);
        color: #E5332A;
      }

      .faq-answer {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.3s ease-out;
      }

      .faq-item.open .faq-answer {
        max-height: 500px;
      }

      .faq-answer p {
        padding: 0 24px 24px;
        color: #495057;
        line-height: 1.7;
      }

      /* No Results */
      .no-results {
        text-align: center;
        padding: 60px 20px;
        color: #868e96;
      }

      .no-results svg {
        margin-bottom: 16px;
        opacity: 0.5;
      }

      /* Contact Box */
      .contact-box {
        max-width: 700px;
        margin: 60px auto 0;
        background: linear-gradient(135deg, #E5332A, #ff4436);
        border-radius: 24px;
        padding: 48px;
        text-align: center;
        color: #fff;
      }

      .contact-box h3 {
        font-size: 28px;
        margin-bottom: 12px;
      }

      .contact-box p {
        font-size: 16px;
        opacity: 0.9;
        margin-bottom: 32px;
      }

      .contact-buttons {
        display: flex;
        gap: 16px;
        justify-content: center;
        flex-wrap: wrap;
      }

      .contact-btn {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 14px 28px;
        background: #fff;
        color: #E5332A;
        border-radius: 12px;
        text-decoration: none;
        font-weight: 600;
        transition: all 0.3s;
      }

      .contact-btn:hover {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
      }

      /* ============ RESPONSIVE ============ */
      @media (max-width: 992px) {
        .hero-content h1 {
          font-size: 40px;
        }

        .categories {
          justify-content: flex-start;
          padding: 0 0 12px 0;
        }

        .category-btn {
          font-size: 13px;
          padding: 10px 20px;
        }
      }

      @media (max-width: 768px) {
        .container {
          padding: 0 16px;
        }

        .page-hero {
          padding: 60px 0;
        }

        .hero-content h1 {
          font-size: 32px;
        }

        .hero-content p {
          font-size: 16px;
        }

        /* Mobil kategori butonları - yatay scroll */
        .categories {
          justify-content: flex-start;
          flex-wrap: nowrap; /* Mobilde wrap'i kapat */
          margin-bottom: 32px;
          padding: 0 0 12px 0;
          /* Fade efekti için gradient */
          position: relative;
        }

        /* Sağda fade efekti (opsiyonel) */
        .categories::after {
          content: '';
          position: absolute;
          right: 0;
          top: 0;
          bottom: 12px;
          width: 40px;
          background: linear-gradient(to right, transparent, #f8f9fa);
          pointer-events: none;
        }

        .category-btn {
          font-size: 13px;
          padding: 10px 18px;
          border-radius: 10px;
        }

        /* FAQ öğeleri */
        .faq-item {
          border-radius: 12px;
        }

        .faq-question {
          padding: 18px;
          font-size: 15px;
        }

        .faq-answer p {
          padding: 0 18px 18px;
          font-size: 14px;
        }

        /* Contact box */
        .contact-box {
          padding: 32px 24px;
          border-radius: 20px;
        }

        .contact-box h3 {
          font-size: 24px;
        }

        .contact-box p {
          font-size: 14px;
          margin-bottom: 24px;
        }

        .contact-buttons {
          flex-direction: column;
          gap: 12px;
        }

        .contact-btn {
          width: 100%;
          justify-content: center;
          padding: 12px 24px;
          font-size: 14px;
        }

        /* Search box */
        .faq-search-box input {
          padding: 14px 16px 14px 48px;
          font-size: 15px;
          border-radius: 12px;
        }

        .faq-search-icon {
          left: 16px;
        }
      }

      @media (max-width: 480px) {
        .hero-content h1 {
          font-size: 28px;
        }

        .hero-content p {
          font-size: 14px;
        }

        .category-btn {
          font-size: 12px;
          padding: 8px 16px;
        }

        .contact-box {
          padding: 24px 20px;
        }

        .contact-box h3 {
          font-size: 20px;
        }
      }
    `}</style>
  );
}